import { Chart } from "@/ui/organisms/Chart/Chart";
import { GroupedCategoryAxis } from "@/ui/organisms/Chart/Axis";
import {
    filterNullValuesFromChartData,
    formatCurrency,
} from "./utils/filterChartData";
import { aggregateChartData, PeriodType } from "./utils/periodAggregation";
import { usePeriodFilter } from "./hooks/usePeriodFilter";

export function Liquidity({
    isCondensed,
    data,
}: {
    isCondensed?: boolean;
    data?: any;
}) {
    const { selectedPeriod, periodFilterProps } = usePeriodFilter("monthly");

    const aggregatedData = aggregateChartData(
        data || [],
        selectedPeriod.value as PeriodType,
        "liquidity",
    );

    const filteredData = filterNullValuesFromChartData(aggregatedData);

    if (filteredData.length === 0) {
        return null;
    }

    const getMaxValue = (data: any[], keys: string[]) => {
        let max = 0;
        data.forEach((item) => {
            keys.forEach((key) => {
                if (
                    item[key] !== null &&
                    item[key] !== undefined &&
                    item[key] !== Infinity
                ) {
                    max = Math.max(max, Math.abs(item[key]));
                }
            });
        });
        return max;
    };

    const getDecimalPlaces = (maxValue: number) => {
        if (maxValue >= 1) return 0;
        if (maxValue >= 0.1) return 1;
        if (maxValue >= 0.01) return 2;
        if (maxValue >= 0.001) return 3;
        if (maxValue >= 0.0001) return 4;
        return 5;
    };

    const yAxisKeys = ["runway", "quickRatio", "currentRatio", "cashRatio"];
    const maxValue = getMaxValue(filteredData, yAxisKeys);
    const decimalPlaces = getDecimalPlaces(maxValue);

    return (
        <Chart
            title="Liquidity"
            description="Despite healthy liquid asset levels, persistent low current, quick, and cash ratios well below 1, frequent negative net income, and runway volatility indicate chronic liquidity stress and a high risk profile."
            isCondensed={isCondensed}
            periodFilter={periodFilterProps}
            options={{
                data: filteredData,
                tooltip: {
                    mode: "single",
                },
                series: [
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "runway",
                        yName: "Runway (Months)",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const runwayValue =
                                    datum.runway === Infinity
                                        ? "∞"
                                        : datum.runway.toFixed(1);

                                return {
                                    heading: `${month}`,
                                    title: "Runway (Months)",
                                    data: [
                                        {
                                            label: "Runway (Months)",
                                            value: `${runwayValue} months`,
                                        },
                                        {
                                            label: "Liquid Assets",
                                            value: formatCurrency(
                                                datum.liquidAssets,
                                            ),
                                        },
                                        {
                                            label: "Total Current Liabilities",
                                            value: formatCurrency(
                                                datum.totalCurrentLiabilities,
                                            ),
                                        },
                                        {
                                            label: "Cash",
                                            value: formatCurrency(datum.cash),
                                        },
                                        {
                                            label: "Total Revenue",
                                            value: formatCurrency(
                                                datum.totalRevenue,
                                            ),
                                        },
                                        {
                                            label: "Total Expenses",
                                            value: formatCurrency(
                                                datum.totalExpenses,
                                            ),
                                        },
                                        {
                                            label: "EBITDA",
                                            value: formatCurrency(datum.ebitda),
                                        },
                                        {
                                            label: "Net Income",
                                            value: formatCurrency(
                                                datum.netIncome,
                                            ),
                                        },
                                    ],
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "quickRatio",
                        yName: "Quick Ratio",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;

                                return {
                                    heading: `${month}`,
                                    title: "Quick Ratio",
                                    data: [
                                        {
                                            label: "Quick Ratio",
                                            value: `${datum.quickRatio.toFixed(2)}`,
                                        },
                                        {
                                            label: "Current Assets",
                                            value: `$${datum.currentAssets.toLocaleString()}`,
                                        },
                                        {
                                            label: "Current Assets Net Inventory",
                                            value: `$${datum.currentAssetsNetInventory.toLocaleString()}`,
                                        },
                                        {
                                            label: "Total Current Liabilities",
                                            value: `$${datum.totalCurrentLiabilities.toLocaleString()}`,
                                        },
                                    ],
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "currentRatio",
                        yName: "Current Ratio",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;

                                return {
                                    heading: `${month}`,
                                    title: "Current Ratio",
                                    data: [
                                        {
                                            label: "Current Ratio",
                                            value: `${datum.currentRatio.toFixed(2)}`,
                                        },
                                        {
                                            label: "Current Assets",
                                            value: `$${datum.currentAssets.toLocaleString()}`,
                                        },
                                        {
                                            label: "Total Current Liabilities",
                                            value: `$${datum.totalCurrentLiabilities.toLocaleString()}`,
                                        },
                                    ],
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "cashRatio",
                        yName: "Cash Ratio",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;

                                return {
                                    heading: `${month}`,
                                    title: "Cash Ratio",
                                    data: [
                                        {
                                            label: "Cash Ratio",
                                            value: `${datum.cashRatio.toFixed(2)}`,
                                        },
                                        {
                                            label: "Cash",
                                            value: `$${datum.cash.toLocaleString()}`,
                                        },
                                        {
                                            label: "Total Current Liabilities",
                                            value: `$${datum.totalCurrentLiabilities.toLocaleString()}`,
                                        },
                                    ],
                                };
                            },
                        },
                    },
                ],
                axes: [
                    GroupedCategoryAxis.doubleDepth,
                    {
                        type: "number",
                        position: "right",
                        min: 0,
                        label: {
                            formatter: (params) => {
                                if (params.value === Infinity) return "∞";

                                if (params.value >= 1000) {
                                    const kValue = params.value / 1000;
                                    return `${kValue.toFixed(decimalPlaces)}K`;
                                }

                                return params.value.toFixed(decimalPlaces);
                            },
                        },
                        keys: yAxisKeys,
                    },
                ],
            }}
        />
    );
}
